"use client";
import { formatAreaSize, formatCurrency } from "@/app/helpers/numberHelper";
import { iRequestContext } from "@/app/models/contextModels/requestContext";
import { iMyPagesStorageData } from "@/app/models/myPages/myPagesStorageData";
import Modal from "../Modal/Modal";
import { useEffect, useState } from "react";
import ChangeInsurance from "./Modals/ChangeInsurance";
import TerminateStorageUnit from "./Modals/TerminateStorageUnit";
import Accordion from "../Accordion/Accordion";
import styles from "./mypages.module.scss";
import Loader from "../Loader/Loader";
import { formatDateByCountry } from "@/app/helpers/dateHelper";
import { request } from "http";
import { eTarget } from "@/app/models/enum/target";
import LinkElement from "../General/LinkElement";
import { eChevronPosition } from "@/app/models/enum/chevronPosition";
import { eButtonColorTheme } from "@/app/models/enum/eButtonColorTheme";
import { StartSigningProcedure } from "@/app/helpers/clientUtilityHelper";
import { clientLogError } from "@/app/helpers/clientLoggingHelper";
import { iGetSiteByIdRequestModel } from "@/app/models/apiModels/requestModels/getSiteByIdRequestModel";
import { useSearchParams, useRouter } from 'next/navigation';
import { iPolicyContent } from "@/app/models/policyContent";

interface Props {
  storageUnits: iMyPagesStorageData[];
  requestContext: iRequestContext;
  customerId: string;
  canChangeUnit: boolean;
  policyContent?: iPolicyContent;
}

interface iStorageKeysProps {
  unitNumber: string;
  facilityCode?: string;
  unitCode?: string;
  accessPhone?: string;
}

export default function StorageUnitsList({
  storageUnits,
  requestContext,
  customerId,
  canChangeUnit,
  policyContent
}: Props) {
  const [isInsuranceModalOpen, setIsInsuranceModalOpen] = useState(false);
  const [isTerminateModalOpen, setIsTerminateModalOpen] = useState(false);
  const [selectedStorageUnit, setSelectedStorageUnit] =
    useState<iMyPagesStorageData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [storageKeyData, setStorageKeyData] = useState<iStorageKeysProps | undefined>(undefined);
  const [showStorageKeyPopup, setShowStorageKeyPopup] = useState(false);
  const router = useRouter();
  const [showError, setShowError] = useState<boolean>(false);
  const [errorTitle, setErrorTitle] = useState<string>("");
  const [errorText, setErrorText] = useState<string>("");
  const [reroutingHref, setReroutingHref] = useState<string>("");
  const searchParams = useSearchParams();

  useEffect(() => {
    const signingStatus = searchParams.get('signingStatus');
    if (signingStatus === 'failed') {
      initError(requestContext.translations.pages.order.paymentContainer.signNotOK, requestContext.translations.pages.order.paymentContainer.signNotOKText);
    }
  }, [searchParams]);

  const handleOpenInsuranceModal = (storageUnit: iMyPagesStorageData) => {
    setSelectedStorageUnit(storageUnit);
    setIsInsuranceModalOpen(true);
  };

  const handleCloseInsuranceModal = () => {
    setIsInsuranceModalOpen(false);
    setIsLoading(true);
    location.reload();
  };

  const handleOpenTerminateModal = (storageUnit: iMyPagesStorageData) => {
    setSelectedStorageUnit(storageUnit);
    setIsTerminateModalOpen(true);
  };

  const handleCloseTerminateModal = () => {
    setIsTerminateModalOpen(false);
    setIsLoading(true);
    location.reload();
  };

  const handleStorageKeyPopup = (unitNumber: string, facilityCode?: string, unitCode?: string, accessPhone?: string) => {
    setStorageKeyData({ unitNumber, facilityCode, unitCode, accessPhone });
    setShowStorageKeyPopup(true);
  }

  const closeStorageKeyPopup = () => {
    setShowStorageKeyPopup(false);
    setStorageKeyData(undefined);
  }

  const showMainLoader = () => {
    setIsLoading(true);
  };

  const doSignContract = async (storageUnit: iMyPagesStorageData) => {
    setIsLoading(true);

    const requestModel: iGetSiteByIdRequestModel = {
      country: requestContext.country,
      facilityId: storageUnit.siteData.id,
      showOffline: true
    };

    const response = await fetch('/api/site/getSiteById', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestModel),
    });

    if (!response.ok) {
      throw new Error('Network response was not ok');
    }

    const data = await response.json();
    const siteCode = data?.code || "";

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "signing_contract",
      step: "start_signing",
      site_code: storageUnit.siteData.id,
      site_id: siteCode,
      site_name: storageUnit.siteData.name,
      size: storageUnit.unitData.size,
      unit_number: storageUnit.unitData.unitNumber,
      contract_id: storageUnit.contractId,
      customer_id: customerId,
      country: requestContext.country
    });

    const signingResponse = await StartSigningProcedure(
      storageUnit.contractId,
      requestContext.country,
      customerId,
      requestContext.language,
      `${requestContext.menu.myPagesLoggedInLink.url}?signingStatus=failed`,
      requestContext.menu.myPagesLoggedInLink.url
    );

    if (signingResponse === null || signingResponse === undefined) {
      clientLogError("StorageUnitList.tsx", "doSignContract", "doSignContract response not ok", "contractId", storageUnit.contractId, "customerId", customerId);
      initError(requestContext.translations.pages.order.paymentContainer.signNotOK, requestContext.translations.pages.order.paymentContainer.signNotOKText);
      return;
    }

    router.push(signingResponse);
  }

  const initError = (title: string = "", message: string = "", href: string = "") => {

    if (title === "") {
      title = requestContext.translations.general.errors.e500.title;
    }

    if (message === "") {
      message = requestContext.translations.general.errors.e500.message;
    }

    if (href === "") {
      href = requestContext.menu.myPagesLoggedInLink.url;
    }

    setErrorTitle(title);
    setErrorText(message);
    setShowError(true);
    setReroutingHref(href);
    setIsLoading(false);
  }

  const clearError = () => {
    setShowError(false);
    setErrorTitle("");
    setErrorText("");
    if (reroutingHref !== "") {
      router.push(reroutingHref);
    }
  }

  const getStorageStatus = (storageUnit: iMyPagesStorageData) => {
    const notSignedContractStatus = requestContext.translations.pages.myPagesPage.notSignedContractStatus;
    if (requestContext.country.toLowerCase() === "no" && !storageUnit.isContractSigned && notSignedContractStatus !== "") {
      return notSignedContractStatus;
    }


    return (!storageUnit.unitData.unitNoticeLeaveDate || storageUnit.unitData.unitNoticeLeaveDate === "")
      ? storageUnit.unitData.startDate === null
        ? requestContext.translations.pages.myPagesPage.booked
        : `${requestContext.translations.pages.myPagesPage.bookedFrom} ${formatDateByCountry(storageUnit.unitData.startDate, requestContext.country)}`
      : `${requestContext.translations.pages.myPagesPage.unitCancelledFrom} ${formatDateByCountry(storageUnit.unitData.unitNoticeLeaveDate, requestContext.country)}`
  }

  return (
    <div className="rounded-3xl bg-white p-6 mb-10">
      <h2 className="mb-4 lg:mb-10">
        {requestContext.translations.pages.myPagesPage.myStorageUnits}
      </h2>
      {storageUnits.some((storageUnit) => storageUnit.unitData.accessCode || storageUnit.unitData.codeLockerCode || storageUnit.unitData.accessPhoneNumber) && (
        <div className="flex gap-2 align-center textSmall mb-4">
          <span className="bg-gs-green rounded-3xl sm:rounded-4xl p-1 md:p-2"><img className="w-[50px] sm:w-[20px]" width={20} src={`/assets/img/icons/no_key_icon.svg`} alt="key icon" /></span>
          <span>{requestContext.translations.pages.myPagesPage.clickKeyIconText}</span>
        </div>
      )}
      <div className="hidden lg:grid grid-cols-12 gap-1 xl:gap-4 px-2 xl:px-4 py-2 rounded-4xl bg-light-gs-green">
        <div className="col-span-3">
          <h3 className="mb-0">{requestContext.translations.pages.myPagesPage.unit}</h3>
        </div>
        <div className="col-span-1">
          <h3 className="mb-0">{requestContext.translations.pages.myPagesPage.number}</h3>
        </div>
        <div className="col-span-2">
          <h3 className="mb-0">{requestContext.translations.general.price}</h3>
        </div>
        <div className="col-span-2">
          <h3 className="mb-0">{requestContext.translations.pages.myPagesPage.status}</h3>
        </div>
        <div className="col-span-4"></div>
      </div>
      <Loader
        loading={isLoading}
        useFullWindow={true}
        useNoBackground={false}
      />
      <div className="hidden lg:grid">
        {storageUnits.map((storageUnit, index) => (
          <div
            key={index}
            className={`grid grid-cols-12 gap-1 xl:gap-4 mt-4 px-2 xl:px-4 py-2 rounded-4xl border border-green ${index % 2 === 1 ? "bg-light-gs-green" : ""
              }`}
          >
            <div className="col-span-3 flex items-center">
              <div>
                <div className="font-medium pr-1 w-full inline-block">
                  {storageUnit.siteData.name}
                </div>
                <div className="w-full inline-block">
                  {`${storageUnit.unitData.size} - ${formatAreaSize(
                    storageUnit.unitData.areaSize
                  )}`}
                  m<span className="align-super text-[10.5px]">2</span>
                </div>
              </div>
            </div>
            <div className="col-span-1 flex items-center flex-wrap">
              {(storageUnit.unitData.accessCode || storageUnit.unitData.codeLockerCode || storageUnit.unitData.accessPhoneNumber) && storageUnit.isContractSigned && (
                <div className="mr-1 flex-shrink-0">
                  <LinkElement
                    title={requestContext.translations.pages.myPagesPage.clickKeyIconText}
                    isButton={true}
                    onClick={() => handleStorageKeyPopup(storageUnit.unitData.unitNumber, storageUnit.unitData.accessCode, storageUnit.unitData.codeLockerCode, storageUnit.unitData.accessPhoneNumber)}
                    className="btn !p-0 lg:!p-1 xl:!p-2 link-size-standard"
                  >
                    <img className="inline-block !w-[20px] h-[20px]" width={20} src="/assets/img/icons/no_key_icon.svg" alt="key icon" />
                  </LinkElement>
                </div>
              )}
              {storageUnit.unitData.unitNumber}
            </div>
            <div className="col-span-2 flex items-center">{`${formatCurrency(
              storageUnit.unitData.price,
              requestContext.translations
            )}`}</div>
            <div className="col-span-2 flex items-center">
              {getStorageStatus(storageUnit)}
            </div>
            <div className="col-span-4 gap-1 xl:gap-2 flex justify-between">
              {((requestContext.country.toLowerCase() === "no" && storageUnit.isContractSigned) || requestContext.country.toLowerCase() !== "no") ? (
                <>
                  {(!storageUnit.unitData.unitNoticeLeaveDate || storageUnit.unitData.unitNoticeLeaveDate === "") && canChangeUnit && (
                    <button
                      onClick={() => {
                        showMainLoader();
                        window.location.href = `${requestContext.configurations.pages.changeStorageUnitPage.url}?unitNumber=${storageUnit.unitData.unitNumber}&siteId=${storageUnit.siteData.id}&size=${storageUnit.unitData.size}&customerId=${customerId}`;
                      }}
                      className="btn nowrap flex-grow lg:!p-[10px]"
                    >
                      {
                        requestContext.translations.pages.myPagesPage
                          .changeStorageUnit
                      }
                    </button>
                  )}

                  <LinkElement
                    href={`${requestContext.apiV2Url}/users/contract/${storageUnit.contractId}/${customerId}`}
                    target={eTarget.Blank}
                    chevronPosition={eChevronPosition.None}
                    buttonColorTheme={eButtonColorTheme.DarkContrast}
                    isButton={true}
                    title={requestContext.translations.pages.myPagesPage.leaseAgreement}
                    className="lg:!p-[10px] !flex flex-grow xl:flex-grow-0 align-center justify-center"
                  >
                    <span>{requestContext.translations.pages.myPagesPage.leaseAgreement}</span>
                  </LinkElement>

                  {storageUnit.unitData.unitNoticeLeaveDate == null && (
                    <>
                      <button
                        onClick={() => handleOpenInsuranceModal(storageUnit)}
                        className="btn black flex-grow lg:!p-[10px]"
                      >
                        {requestContext.translations.general.insurance}
                      </button>
                      <button
                        onClick={() => handleOpenTerminateModal(storageUnit)}
                        className="btn dark-contrast nowrap flex-grow lg:!p-[10px]"
                      >
                        {requestContext.translations.pages.myPagesPage.terminate}
                      </button>
                    </>
                  )}
                </>
              ) : (
                <>
                  <LinkElement
                    target={eTarget.Blank}
                    chevronPosition={eChevronPosition.None}
                    buttonColorTheme={eButtonColorTheme.Green}
                    isButton={true}
                    title={requestContext.translations.pages.myPagesPage.notSignedContract}
                    className="lg:!p-[10px] !flex align-center w-full text-center"
                    onClick={() => doSignContract(storageUnit)}
                  >
                    <span className="w-full">{requestContext.translations.pages.myPagesPage.notSignedContract}</span>
                  </LinkElement>
                </>
              )}

            </div>
          </div>
        ))}
      </div>
      <div className="grid gap-4 lg:hidden">
        {storageUnits.map((storageUnit, index) => (
          <Accordion
            isExpandedOnLoad={index === 0}
            key={index}
            title={`${storageUnit.siteData.name} - ${storageUnit.unitData.size} - ${formatAreaSize(storageUnit.unitData.areaSize)}`}
            titleElement={
              <div
                className={`col-span-4 flex items-center ${styles.mobileHeader}`}
                data-header={requestContext.translations.pages.myPagesPage.unit}
              >
                <div>
                  <div className="font-medium pr-1 w-full">
                    {storageUnit.siteData.name}
                  </div>
                  <div className="w-full">
                    {`${storageUnit.unitData.size} - ${formatAreaSize(
                      storageUnit.unitData.areaSize
                    )}`}
                    m<span className="align-super text-[10.5px]">2</span>
                  </div>
                </div>
              </div>
            }
            translations={requestContext.translations}
            className={`${styles.mobileInvoiceAccordion} rounded-[16px]`}
          >
            <div key={index} className={`grid grid-cols-2 sm:grid-cols-3 gap-4`}>
              <div
                className={`col-span-2 sm:col-span-1 flex items-center ${styles.mobileHeader}`}
                data-header={
                  requestContext.translations.pages.myPagesPage.number
                }
              >
                {(storageUnit.unitData.accessCode || storageUnit.unitData.codeLockerCode || storageUnit.unitData.accessPhoneNumber) && storageUnit.isContractSigned && (
                  <div className="mr-1">
                    <LinkElement
                      title={requestContext.translations.pages.myPagesPage.clickKeyIconText}
                      isButton={true}
                      onClick={() => handleStorageKeyPopup(storageUnit.unitData.unitNumber, storageUnit.unitData.accessCode, storageUnit.unitData.codeLockerCode, storageUnit.unitData.accessPhoneNumber)}
                      className="btn !p-2 link-size-standard"
                    >
                      <img className="inline-block !w-[30px] h-[30px]" width={20} src="/assets/img/icons/no_key_icon.svg" alt="key icon" />
                    </LinkElement>
                  </div>
                )}
                {storageUnit.unitData.unitNumber}
              </div>
              <div
                className={`sm:col-span-1 flex items-center ${styles.mobileHeader}`}
                data-header={requestContext.translations.general.price}
              >{`${formatCurrency(
                storageUnit.unitData.price,
                requestContext.translations
              )}`}</div>

              <div
                className={`col-span-3 sm:col-span-1 flex items-center ${styles.mobileHeader}`}
                data-header={
                  requestContext.translations.pages.myPagesPage.status
                }
              >
                {getStorageStatus(storageUnit)}
              </div>

              <div className="col-span-3 flex flex-col sm:grid sm:grid-cols-2 gap-4 items-center justify-end">
                {((requestContext.country.toLowerCase() === "no" && storageUnit.isContractSigned) || requestContext.country.toLowerCase() !== "no") ? (
                  <>
                    {(!storageUnit.unitData.unitNoticeLeaveDate || storageUnit.unitData.unitNoticeLeaveDate === "") && canChangeUnit && (
                      <button
                        onClick={() => {
                          window.location.href = `${requestContext.configurations.pages.changeStorageUnitPage.url}?unitNumber=${storageUnit.unitData.unitNumber}&siteId=${storageUnit.siteData.id}&size=${storageUnit.unitData.size}&customerId=${customerId}`;
                        }}
                        className="btn w-full text-center"
                      >
                        {
                          requestContext.translations.pages.myPagesPage
                            .changeStorageUnit
                        }
                      </button>
                    )}
                    <LinkElement
                      href={`${requestContext.apiV2Url}/users/contract/${storageUnit.contractId}/${customerId}`}
                      target={eTarget.Blank}
                      chevronPosition={eChevronPosition.None}
                      buttonColorTheme={eButtonColorTheme.Green}
                      isButton={true}
                      title={requestContext.translations.pages.myPagesPage.leaseAgreement}
                      className="w-full text-center dark-contrast"
                    >
                      {requestContext.translations.pages.myPagesPage.leaseAgreement}
                    </LinkElement>
                    {storageUnit.unitData.unitNoticeLeaveDate == null && (
                      <>
                        <button
                          onClick={() => handleOpenInsuranceModal(storageUnit)}
                          className="btn w-full black"
                        >
                          {requestContext.translations.general.insurance}
                        </button>
                        <button
                          onClick={() => handleOpenTerminateModal(storageUnit)}
                          className="btn w-full dark-contrast"
                        >
                          {requestContext.translations.pages.myPagesPage.terminate}
                        </button>
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <LinkElement
                      target={eTarget.Blank}
                      chevronPosition={eChevronPosition.None}
                      buttonColorTheme={eButtonColorTheme.Green}
                      isButton={true}
                      title={requestContext.translations.pages.myPagesPage.notSignedContract}
                      className="lg:!p-[10px] !flex align-center w-full text-center"
                      onClick={() => doSignContract(storageUnit)}
                    >
                      <span className="w-full">{requestContext.translations.pages.myPagesPage.notSignedContract}</span>
                    </LinkElement>
                  </>
                )}
              </div>
            </div>
          </Accordion>
        ))}
      </div>
      <Modal
        isOpen={showStorageKeyPopup}
        onClose={closeStorageKeyPopup}
        closeTranslation={requestContext.translations.general.closePopup}
        className="w-300-responsive"
      >
        {storageKeyData && (
          <div className="w-full text-left">
            <h3>{requestContext.translations.pages.myPagesPage.accessInfoFor} {storageKeyData.unitNumber}</h3>
            {storageKeyData.accessPhone && (
              <p>{requestContext.translations.pages.myPagesPage.accessPhone}: {storageKeyData.accessPhone}</p>
            )}
            {storageKeyData.facilityCode && (
              <p>{requestContext.translations.pages.myPagesPage.facilityCode}: {storageKeyData.facilityCode}</p>
            )}
            <p>{requestContext.translations.pages.myPagesPage.unitNumber}: {storageKeyData.unitNumber}</p>
            {storageKeyData.unitCode && (
              <p>{requestContext.translations.pages.myPagesPage.unitCode}: {storageKeyData.unitCode}</p>
            )}
          </div>
        )}
      </Modal>
      <Modal
        isOpen={isInsuranceModalOpen}
        onClose={handleCloseInsuranceModal}
        closeTranslation={requestContext.translations.general.closePopup}
        className="w-500-responsive"
      >
        {selectedStorageUnit && (
          <ChangeInsurance
            onClose={handleCloseInsuranceModal}
            requestContext={requestContext}
            storageUnit={selectedStorageUnit}
            customerId={customerId}
          />
        )}
      </Modal>
      <Modal
        isOpen={isTerminateModalOpen}
        onClose={handleCloseTerminateModal}
        closeTranslation={requestContext.translations.general.closePopup}
        className="w-500-responsive"
      >
        {selectedStorageUnit && (
          <TerminateStorageUnit
            onClose={handleCloseTerminateModal}
            requestContext={requestContext}
            storageUnit={selectedStorageUnit}
            customerId={customerId}
            policyContent={policyContent}
          />
        )}
      </Modal>
      <Modal
        isOpen={showError}
        onClose={clearError}
        closeTranslation={requestContext.translations.general.closePopup}
      >
        <div>
          <h2>{errorTitle}</h2>
          <div>{errorText}</div>
          <LinkElement
            title={requestContext.translations.general.close}
            isButton={true}
            onClick={clearError}
            buttonColorTheme={eButtonColorTheme.DarkContrast}
            className="w-full mt-6"
          >
            {requestContext.translations.general.close}
          </LinkElement>
        </div>
      </Modal>
    </div>
  );
}
